import { Button } from '@/components/ui/button';
import { useState, useEffect } from 'react';
import { db } from '@/lib/firebase/firebaseClient';
import { collection, doc, getDoc, getDocs } from 'firebase/firestore';
import { Question } from '@/types/questions';
import { updateQuestionImage } from '@/lib/firebase';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Pencil, Trash, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface QuestionWithImage {
  id: string;
  subject: string;
  year: string;
  question: string;
  imageUrl: string;
}

// Add type for error handling
interface FirebaseError extends Error {
  code?: string;
  message: string;
}

const UploadQuestion = () => {
  const [questions, setQuestions] = useState<QuestionWithImage[]>([]);
  const [subject, setSubject] = useState('');
  const [year, setYear] = useState('');
  const [years, setYears] = useState<string[]>([]);
  const [yearQuestions, setYearQuestions] = useState<Question[]>([]);
  const [selectedQuestion, setSelectedQuestion] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<QuestionWithImage | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editImageUrl, setEditImageUrl] = useState('');

  // Fetch available years when subject is selected
  useEffect(() => {
    const fetchYears = async () => {
      if (!subject) return;
      try {
        const subjectRef = collection(db, subject.toLowerCase());
        const yearDocs = await getDocs(subjectRef);
        const availableYears = yearDocs.docs.map(doc => doc.id);
        setYears(availableYears.sort());
        setYear('');
        setYearQuestions([]);
        setSelectedQuestion('');
      } catch (error) {
        const fbError = error as FirebaseError;
        console.error('Error:', fbError.message);
      }
    };

    fetchYears();
  }, [subject]);

  // Fetch questions when year is selected
  useEffect(() => {
    const fetchQuestions = async () => {
      if (!subject || !year) return;
      try {
        const subjectRef = collection(db, subject.toLowerCase());
        const yearDocRef = doc(subjectRef, year);
        const yearDocSnap = await getDoc(yearDocRef);

        if (yearDocSnap.exists()) {
          const data = yearDocSnap.data();
          let questionsArray = [];
          
          if (data && data.questions) {
            if (Array.isArray(data.questions)) {
              questionsArray = data.questions;
            } else if (typeof data.questions === 'object') {
              questionsArray = Object.values(data.questions);
            }
          }

          const formattedQuestions = questionsArray.map((q: any, index: number) => ({
            id: String(index + 1),
            text: q.question || '',
            question: q.question || '',
            options: q.options || [],
            answer: q.answer || '',
            imageUrl: q.imageUrl || ''
          }));
          
          setYearQuestions(formattedQuestions);
        }
      } catch (error) {
        const fbError = error as FirebaseError;
        console.error('Error:', fbError.message);
      }
    };

    fetchQuestions();
  }, [subject, year]);

  // Add this useEffect to fetch all questions with images
  useEffect(() => {
    const fetchQuestionsWithImages = async () => {
      try {
        const allQuestions: QuestionWithImage[] = [];
        const subjects = ['mathematics', 'english', 'physics', 'chemistry', 'biology'];
        
        for (const subject of subjects) {
          const subjectRef = collection(db, subject.toLowerCase());
          const yearDocs = await getDocs(subjectRef);
          
          for (const yearDoc of yearDocs.docs) {
            const yearData = yearDoc.data();
            let questions = [];
            
            if (yearData && yearData.questions) {
              if (Array.isArray(yearData.questions)) {
                questions = yearData.questions;
              } else if (typeof yearData.questions === 'object') {
                questions = Object.values(yearData.questions);
              }
            }
            
            const questionsWithImages = questions
              .map((q: any, index: number) => ({
                id: String(index + 1),
                subject: subject,
                year: yearDoc.id,
                question: q.question || '',
                imageUrl: q.imageUrl || ''
              }))
              .filter((q: QuestionWithImage) => q.imageUrl !== '');
            
            allQuestions.push(...questionsWithImages);
          }
        }
        
        setQuestions(allQuestions);
      } catch (error) {
        const fbError = error as FirebaseError;
        console.error('Error:', fbError.message);
      }
    };

    fetchQuestionsWithImages();
  }, []);

  // Add this to refresh the questions list after upload or delete
  const refreshQuestionsList = async () => {
    try {
      const allQuestions: QuestionWithImage[] = [];
      const subjects = ['mathematics', 'english', 'physics', 'chemistry', 'biology'];
      
      for (const subject of subjects) {
        const subjectRef = collection(db, subject);
        const yearDocs = await getDocs(subjectRef);
        
        for (const yearDoc of yearDocs.docs) {
          const yearData = yearDoc.data();
          let questions = [];
          
          if (yearData && yearData.questions) {
            if (Array.isArray(yearData.questions)) {
              questions = yearData.questions;
            } else if (typeof yearData.questions === 'object') {
              questions = Object.values(yearData.questions);
            }
          }
          
          const questionsWithImages = questions
            .map((q: any, index: number) => ({
              id: String(index + 1),
              subject: subject,
              year: yearDoc.id,
              question: q.question || '',
              imageUrl: q.imageUrl || ''
            }))
            .filter((q: QuestionWithImage) => q.imageUrl !== '');
          
          allQuestions.push(...questionsWithImages);
        }
      }
      
      setQuestions(allQuestions);
    } catch (error) {
      const fbError = error as FirebaseError;
      console.error('Error:', fbError.message);
    }
  };

  const handleUpload = async () => {
    if (!subject || !year || !selectedQuestion || !imageUrl) {
      alert('Please fill in all required fields');
      return;
    }

    setUploading(true);
    try {
      await updateQuestionImage(subject, year, selectedQuestion, imageUrl);
      setIsOpen(false);
      // Refresh questions list after upload
      await refreshQuestionsList();
    } catch (error) {
      const fbError = error as FirebaseError;
      console.error('Error:', fbError.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (subject: string, year: string, questionId: string) => {
    if (confirm('Are you sure you want to delete this image?')) {
      try {
        await updateQuestionImage(subject, year, questionId, '');
        // Refresh questions list after delete
        await refreshQuestionsList();
      } catch (error) {
        const fbError = error as FirebaseError;
        console.error('Error:', fbError.message);
      }
    }
  };

  const handleEdit = async () => {
    if (!editingQuestion || !editImageUrl) return;

    setUploading(true);
    try {
      await updateQuestionImage(
        editingQuestion.subject,
        editingQuestion.year,
        editingQuestion.id,
        editImageUrl
      );
      setIsEditDialogOpen(false);
      await refreshQuestionsList();
    } catch (error) {
      const fbError = error as FirebaseError;
      console.error('Error:', fbError.message);
    } finally {
      setUploading(false);
    }
  };

  const openEditDialog = (question: QuestionWithImage) => {
    setEditingQuestion(question);
    setEditImageUrl(question.imageUrl);
    setIsEditDialogOpen(true);
  };

  return (
    <div className="container py-8 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold">Question</h1>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button className="bg-purple-600 hover:bg-purple-700">
              Upload question
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Upload Question Image</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Subject</label>
                <Select value={subject} onValueChange={setSubject}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select subject" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mathematics">Mathematics</SelectItem>
                    <SelectItem value="english">English</SelectItem>
                    <SelectItem value="physics">Physics</SelectItem>
                    <SelectItem value="chemistry">Chemistry</SelectItem>
                    <SelectItem value="biology">Biology</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {subject && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Year</label>
                  <Select value={year} onValueChange={setYear}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select year" />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map((yearOption) => (
                        <SelectItem key={yearOption} value={yearOption}>
                          {yearOption}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {year && yearQuestions.length > 0 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Question</label>
                  <Select value={selectedQuestion} onValueChange={setSelectedQuestion}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select question" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[200px]">
                      {yearQuestions.map((q: Question) => (
                        <SelectItem key={q.id} value={q.id}>
                          {`Q${q.id}: ${q.question?.substring(0, 60) || 'No question text'}...`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <label className="text-sm font-medium">Image URL</label>
                <Input
                  type="url"
                  placeholder="Enter image URL"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                />
              </div>

              <Button
                className="w-full bg-purple-600 hover:bg-purple-700"
                onClick={handleUpload}
                disabled={uploading || !subject || !year || !selectedQuestion || !imageUrl}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Add Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Question Image</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <p className="text-sm font-medium mb-2">Question</p>
              <p className="text-sm text-gray-600">{editingQuestion?.question}</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Image URL</label>
              <Input
                type="url"
                placeholder="Enter image URL"
                value={editImageUrl}
                onChange={(e) => setEditImageUrl(e.target.value)}
              />
            </div>
            <Button
              className="w-full bg-purple-600 hover:bg-purple-700"
              onClick={handleEdit}
              disabled={uploading || !editImageUrl}
            >
              {uploading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="border rounded-lg">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">Subject</th>
              <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">Year</th>
              <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">Question</th>
              <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">Image URLs</th>
              <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {questions.map((question) => (
              <tr key={question.id}>
                <td className="px-6 py-4 capitalize">{question.subject}</td>
                <td className="px-6 py-4">{question.year}</td>
                <td className="px-6 py-4">{question.question}</td>
                <td className="px-6 py-4">
                  <a 
                    href={question.imageUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    {question.imageUrl}
                  </a>
                </td>
                <td className="px-6 py-4">
                  <div className="flex gap-2">
                    <button
                      onClick={() => openEditDialog(question)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      <Pencil className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(question.subject, question.year, question.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash className="h-5 w-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Admin = () => {
  const navigate = useNavigate();
  
  const handleLogout = () => {
    localStorage.removeItem('isAdminAuthenticated');
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="container py-8 max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-bold">Admin Dashboard</h1>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded"
          >
            <LogOut className="h-5 w-5" />
            Logout
          </button>
        </div>
        <UploadQuestion />
      </div>
    </div>
  );
};

export default Admin; 